import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./CompanySearchBar.module.sass";
import TextInput from "../TextInput";
import Icon from "../Icon";
import { searchCompanies, getCompanyById } from "../../services/company.service";

// Fonction utilitaire pour le debounce
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

const CompanySearchBar = ({ 
  className,
  label = "Rechercher une entreprise",
  placeholder = "Nom de l'entreprise ou SIRET (min. 2 caractères)",
  onSelectCompany,
  showDetails = true,
  required = false,
  initialCompanyId = null
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [forceSearch, setForceSearch] = useState(false);

  // Charger l'entreprise initiale si un ID est fourni
  useEffect(() => {
    const loadInitialCompany = async () => {
      if (initialCompanyId && !selectedCompany) {
        try {
          const company = await getCompanyById(initialCompanyId);
          if (company) {
            setSelectedCompany(company);
            setSearchQuery(company.name || '');
          }
        } catch (error) {
          console.error('Erreur lors du chargement de l\'entreprise:', error);
        }
      }
    };

    loadInitialCompany();
  }, [initialCompanyId]); // Retirer selectedCompany de la dépendance pour éviter les rechargements

  // Fonction de recherche avec debounce
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (query.length >= 2) {
        setIsSearching(true);
        try {
          const results = await searchCompanies(query);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche des entreprises:', error);
          setSearchResults([]);
          setShowResults(false);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300),
    []
  );

  // Effectuer la recherche lorsque la requête change
  useEffect(() => {
    // Toujours permettre la recherche si forceSearch est true
    if (forceSearch) {
      debouncedSearch(searchQuery);
      setForceSearch(false);
      return;
    }
    
    // Ne pas rechercher si la requête correspond exactement au nom de l'entreprise sélectionnée
    if (selectedCompany && searchQuery === selectedCompany.name) {
      return;
    }
    
    // Effectuer la recherche si la requête est assez longue
    if (searchQuery.length >= 2) {
      debouncedSearch(searchQuery);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery, selectedCompany, forceSearch, debouncedSearch]);

  // Gérer la sélection d'une entreprise
  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setSearchQuery(company.name);
    setShowResults(false);
    
    // Transmettre l'entreprise sélectionnée au composant parent
    if (onSelectCompany) {
      onSelectCompany(company);
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    // Toujours mettre à jour la valeur du champ de recherche
    const newValue = e.target.value;
    setSearchQuery(newValue);
    
    // Si la valeur est différente du nom de l'entreprise sélectionnée, réinitialiser la sélection
    if (selectedCompany && newValue !== selectedCompany.name) {
      setSelectedCompany(null);
      if (onSelectCompany) {
        onSelectCompany(null);
      }
    }
    
    // Forcer l'affichage des résultats si la recherche est assez longue
    if (newValue.length >= 2) {
      setForceSearch(true);
    }
  };
  
  // Forcer une nouvelle recherche (utile pour rechercher après pré-sélection)
  const forceNewSearch = () => {
    // Forcer l'affichage des résultats si la recherche est assez longue
    if (searchQuery.length >= 2) {
      setForceSearch(true);
      setShowResults(true);
    }
    
    // Si une entreprise est sélectionnée, permettre de la modifier
    if (selectedCompany) {
      // Ne pas effacer la sélection, juste permettre la recherche
      setForceSearch(true);
    }
  };

  // Réinitialiser la recherche
  const handleReset = () => {
    setSelectedCompany(null);
    setSearchQuery('');
    if (onSelectCompany) {
      onSelectCompany(null);
    }
  };

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <TextInput
            className={styles.field}
            label={label}
            name="company-search"
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={forceNewSearch}
            required={required}
            readOnly={false}
          />
          {searchQuery && (
            <button className={styles.clearButton} onClick={handleReset} type="button" aria-label="Effacer la recherche">
              <Icon name="close-circle" size="20" />
            </button>
          )}
        </div>
        
        {showResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map((company, index) => (
              <div 
                key={company.id || index} 
                className={styles.resultItem}
                onClick={() => handleSelectCompany(company)}
              >
                <div className={styles.resultContent}>
                  <img
                    src={company.logoUrl || "/images/content/company_logo.png"}
                    alt={company.name}
                    className={styles.companyLogo}
                  />
                  <div className={styles.companyInfo}>
                    <div className={styles.companyName}>{company.name}</div>
                    <div className={styles.companyDetails}>
                      <span>
                        {company.siret && `SIRET: ${company.siret}`}
                        {company.sector && ` • ${company.sector}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !selectedCompany && (
          <div className={styles.noResults}>
            Aucune entreprise trouvée
          </div>
        )}
      </div>

      {selectedCompany && showDetails && (
        <div className={styles.selectedCompanyDetails}>
          <h3>Détails de l'entreprise sélectionnée</h3>
          <div className={styles.detailRow}>
            <span className={styles.label}>Nom:</span>
            <span className={styles.value}>{selectedCompany.name}</span>
          </div>
          {selectedCompany.siret && (
            <div className={styles.detailRow}>
              <span className={styles.label}>SIRET:</span>
              <span className={styles.value}>{selectedCompany.siret}</span>
            </div>
          )}
          {selectedCompany.sector && (
            <div className={styles.detailRow}>
              <span className={styles.label}>Secteur:</span>
              <span className={styles.value}>{selectedCompany.sector}</span>
            </div>
          )}
          <div className={styles.detailRow}>
            <span className={styles.label}>Email:</span>
            <span className={styles.value}>{selectedCompany.email || 'Non renseigné'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Téléphone:</span>
            <span className={styles.value}>{selectedCompany.phone || 'Non renseigné'}</span>
          </div>
          {selectedCompany.employee_count && (
            <div className={styles.detailRow}>
              <span className={styles.label}>Nombre d'employés:</span>
              <span className={styles.value}>{selectedCompany.employee_count}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanySearchBar;
